import { LanguageSelect } from '@/components/language-select';
import { useEffect, useState } from 'react';
import { toastManager } from '@/notifications/toasts';
import { IconBrandGoogleFilled } from '@tabler/icons-react';
import * as m from '@/paraglide/messages.js';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PasswordSignInForm } from '@/features/users/password-sign-in';

const AUTH_ERRORS: Record<string, () => string> = {
  access_denied: m.auth_access_denied,
  sso_failed: m.auth_sign_in_failed,
  sso_expired: m.auth_sign_in_expired,
};

/**
 * The first screen anyone sees, so it carries the same marks as the workspace
 * behind it: the ATS tile, the brand gradient the application header draws, and
 * the card surface every panel inside uses. It had been unstyled text on a bare
 * background, which read as a different product from the one you sign in to.
 */
export function SignInPage() {
  const [external, setExternal] = useState(false);
  useEffect(() => {
    const url = new URL(window.location.href);
    const authError = url.searchParams.get('auth_error');
    if (!authError) return;
    toastManager.add({ title: (AUTH_ERRORS[authError] ?? m.auth_sign_in_failed)(), type: 'error' });
    url.searchParams.delete('auth_error');
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background p-3 sm:p-6">
      <div className="w-full max-w-sm">
        <div className="mb-4 flex justify-end">
          <LanguageSelect />
        </div>
        <div className="surface-elevation rounded-xl border border-border bg-card p-4 sm:p-8">
          <div className="flex flex-wrap items-center gap-3">
            {/* The brand gradient rides the mark itself. As a hairline across the
                top of a rounded card it read as a stray rule rather than a rule
                belonging to the card. */}
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-linear-to-br from-brand to-brand-end text-sm font-bold tracking-tight text-primary-foreground"
            >
              ATS
            </span>
            <div className="min-w-0">
              <h1 className="text-base font-semibold tracking-tight sm:text-lg">{m.app_name()}</h1>
              <p className="text-xs text-muted-foreground">ATS digital</p>
            </div>
          </div>

          <p className="mt-6 text-sm leading-6 text-muted-foreground">
            {m.auth_sign_in_description()}
          </p>

          {/* A plain link, not fetch: the OIDC redirect must be a top-level
              navigation. It takes the button's classes rather than its
              component, so the element stays an anchor. */}
          <a
            href="/api/auth/google"
            className={cn(buttonVariants({ size: 'xl', treatment: 'wrap' }), 'mt-6 w-full')}
          >
            <IconBrandGoogleFilled aria-hidden="true" />
            {m.auth_continue_with_google()}
          </a>

          {/* External collaborators have no Workspace account, so the password path has to be
              reachable here — folded away, because it is the exception rather than the rule. */}
          {external ? (
            <PasswordSignInForm />
          ) : (
            <button
              type="button"
              onClick={() => setExternal(true)}
              className="mt-5 w-full text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
            >
              {m.auth_use_password_instead()}
            </button>
          )}
        </div>

        <p className="mt-5 px-1 text-xs leading-5 text-muted-foreground">
          {m.auth_external_collaborators()}
        </p>
      </div>
    </main>
  );
}
